import Quill from 'quill';
import * as linkify from 'linkifyjs';

const Delta = Quill.import('delta');

const EMBED_PLACEHOLDER = '\uFFFC';

const HTTP_SCHEME = /^https?:\/\//i;
const WWW_PREFIX = /^www\./i;

export default class QuillAutoLink {

  constructor(quill, options = {}) {
    this.quill = quill;
    this.mailto = options.mailto === true;
    this.applying = false;

    quill.on(Quill.events.TEXT_CHANGE, this.onTextChange.bind(this));
    quill.on(Quill.events.SELECTION_CHANGE, this.onSelectionChange.bind(this));
  }

  onTextChange(delta, oldDelta, source) {
    if (this.applying || source !== Quill.sources.USER) {
      return;
    }

    const range = this.changedRange(delta);

    if (range === null) {
      return;
    }

    const caret = range.length > 1 ? null : range.index + range.length;
    const lines = this.quill.getLines(range.index, Math.max(range.length, 1));

    lines.forEach((line) => this.reconcileLine(line, caret));
  }

  onSelectionChange(range, oldRange) {
    if (this.applying) {
      return;
    }

    const caret = range ? range.index : null;
    const lines = [];

    if (oldRange) {
      lines.push(...this.quill.getLines(oldRange.index, 1));
    }
    if (range) {
      lines.push(...this.quill.getLines(range.index, 1));
    }

    [...new Set(lines)].forEach((line) => this.reconcileLine(line, caret));
  }

  flush() {
    this.quill.getLines(0, this.quill.getLength()).forEach((line) => this.reconcileLine(line, null));
  }

  reconcileLine(line, caretIndex) {
    const lineIndex = this.quill.getIndex(line);
    const { text, links } = this.readLine(lineIndex, line.length());
    const matches = linkify.find(text).filter((match) => this.accept(match));
    const frozen = this.frozenWord(text, caretIndex === null ? null : caretIndex - lineIndex);
    const wanted = links.slice();

    this.linkRuns(links).forEach((run) => {
      if (this.overlaps(run, frozen) || !matches.some((match) => this.overlaps(run, match))) {
        return;
      }

      for (let i = run.start; i < run.end; i += 1) {
        wanted[i] = null;
      }
    });

    matches.forEach((match) => {
      if (this.overlaps(match, frozen)) {
        return;
      }

      for (let i = match.start; i < match.end; i += 1) {
        wanted[i] = match.href;
      }
    });

    this.applyLinks(lineIndex, links, wanted);
  }

  readLine(index, length) {
    let text = '';
    const links = [];

    this.quill.getContents(index, length).ops.forEach((op) => {
      const link = op.attributes && op.attributes.link ? op.attributes.link : null;

      if (typeof op.insert === 'string') {
        text += op.insert;
        for (let i = 0; i < op.insert.length; i += 1) {
          links.push(link);
        }
      } else {
        text += EMBED_PLACEHOLDER;
        links.push(link);
      }
    });

    return { text, links };
  }

  applyLinks(lineIndex, links, wanted) {
    const delta = new Delta().retain(lineIndex);

    links.forEach((link, i) => {
      if (wanted[i] === link) {
        delta.retain(1);
      } else {
        delta.retain(1, { link: wanted[i] });
      }
    });

    if (!delta.ops.some((op) => op.attributes)) {
      return;
    }

    this.applying = true;
    try {
      this.quill.updateContents(delta, Quill.sources.USER);
    } finally {
      this.applying = false;
    }
  }

  frozenWord(text, caret) {
    if (caret === null || caret < 0 || caret > text.length) {
      return null;
    }

    let start = caret;
    let end = caret;

    while (start > 0 && !/\s/.test(text[start - 1])) {
      start -= 1;
    }
    while (end < text.length && !/\s/.test(text[end])) {
      end += 1;
    }

    return { start, end };
  }

  linkRuns(links) {
    const runs = [];

    links.forEach((link, i) => {
      const run = runs[runs.length - 1];

      if (link === null) {
        return;
      }
      if (run && run.href === link && run.end === i) {
        run.end = i + 1;
      } else {
        runs.push({ href: link, start: i, end: i + 1 });
      }
    });

    return runs;
  }

  overlaps(a, b) {
    return b !== null && a.start < b.end && b.start < a.end;
  }

  accept(match) {
    if (!match.isLink) {
      return false;
    }
    if (match.type === 'email') {
      return this.mailto;
    }

    return HTTP_SCHEME.test(match.value) || WWW_PREFIX.test(match.value);
  }

  changedRange(delta) {
    let index = 0;
    let length = 0;
    let touched = false;

    delta.ops.forEach((op) => {
      if (op.insert !== undefined) {
        touched = true;
        length += typeof op.insert === 'string' ? op.insert.length : 1;
      } else if (op.delete !== undefined) {
        touched = true;
      } else if (!touched) {
        index += typeof op.retain === 'number' ? op.retain : 1;
      }
    });

    return touched ? { index, length } : null;
  }

}
